import { isMissingAuth } from '@takeshape/schema';
import { deepCloneWithPath, isIterableObject, REMOVE, value } from '@takeshape/util';
import set from 'lodash/set.js';
import { makeRe } from 'minimatch';
import { branding } from "../../config.js";
import { CURRENT_OPEN_API_TRANSFORM_VERSION } from "../../constants.js";
import { addAuthenticationFormConfig, addAuthenticationSchema, authenticationFormConfig, updateAuthentication } from "../../util/authentication.js";
import { hasEveryKey } from "../../util/has.js";
import { addHealthCheckFormConfig, addHealthCheckSchema, getHealthCheck, healthCheckFormConfig, updateHealthCheck } from "../../util/health.js";
import { getInitialData } from "../../util/initial-data.js";
/**
 * Add int64 format to integers so that they can be handled properly downstream
 * @param doc an OpenAPI document
 * @param regexes matching the property names to add int64 to
 */
function createCoerceBigints(regexes) {
    return (value, key, parent) => {
        let newValue = value;
        if (isIterableObject(value)) {
            let target = value;
            let isArray = false;
            if (value.type === 'array' && isIterableObject(value.items)) {
                target = value.items;
                isArray = true;
            }
            if (target.type === 'integer') {
                const name = key === 'schema' && isIterableObject(parent) && typeof parent?.name === 'string' ? parent.name : key;
                if (typeof name === 'string' && regexes.some((regex) => regex.exec(name))) {
                    const updated = { ...target, format: 'int64' };
                    newValue = isArray ? { ...value, items: updated } : updated;
                }
            }
        }
        return newValue;
    };
}
function createRemoveInvalidPatterns() {
    return (value, key) => {
        let newValue = value;
        if (key === 'pattern' && typeof value === 'string') {
            try {
                new RegExp(value, 'u');
            }
            catch {
                newValue = REMOVE;
            }
        }
        return newValue;
    };
}
function transformSpec(doc, options) {
    const removeInvalidPatterns = options.removeInvalidPatterns ? createRemoveInvalidPatterns() : undefined;
    const coerceBigints = options.coerceBigintRegexes?.length
        ? createCoerceBigints(options.coerceBigintRegexes)
        : undefined;
    return deepCloneWithPath(doc, (value, key, parent, path) => {
        let newValue = value;
        if (removeInvalidPatterns) {
            newValue = removeInvalidPatterns(newValue, key, parent, path);
            if (newValue === REMOVE) {
                return newValue;
            }
        }
        if (coerceBigints) {
            newValue = coerceBigints(newValue, key, parent, path);
        }
        return newValue;
    });
}
const serviceProvider = {
    name: 'openapi',
    title: 'OpenAPI',
    description: `Adding a generic OpenAPI (formerly Swagger) service connection allows you to add queries and mutations to your ${branding.productName} project that access a remote service.`,
    links: [
        {
            title: 'Read More',
            url: 'https://en.wikipedia.org/wiki/OpenAPI_Specification'
        },
        { title: 'Homepage', url: 'https://www.openapis.org/' },
        { title: 'Docs', url: '/docs/services/providers/open-api' }
    ],
    serviceType: 'openapi',
    introspectionType: 'openapi',
    createId: value('openapi'),
    getFormSchema: value(addAuthenticationSchema(addHealthCheckSchema({
        type: 'object',
        properties: {
            title: {
                title: 'Name',
                type: 'string'
            },
            id: {
                title: 'Slug',
                type: 'string',
                pattern: '[0-9a-z_-]+'
            },
            namespace: {
                title: 'Namespace',
                type: 'string',
                pattern: '^[A-Z][A-Za-z]+$'
            },
            endpoint: {
                title: 'Endpoint',
                type: 'string',
                pattern: '^https?:\\/\\/'
            },
            spec: {
                title: 'Spec',
                type: 'string',
                pattern: '^https?:\\/\\/'
            },
            coerceBigInts: {
                title: 'Coerce Big Integers',
                type: 'string',
                default: ''
            },
            removeInvalidPatterns: {
                title: 'Remove Invalid Patterns',
                description: 'Remove invalid patterns from the generated GraphQL types. An invalid pattern is one that will trigger a validation error due to invalid escaping or other issues using the RegExp constructor.',
                type: 'boolean',
                default: true
            }
        },
        required: ['title', 'id', 'endpoint', 'spec', 'coerceBigInts', 'removeInvalidPatterns']
    }, { checkNames: ['ping', 'none'] }))),
    getFormConfig: value(addHealthCheckFormConfig(addAuthenticationFormConfig({
        order: ['title', 'id', 'namespace', 'endpoint', 'spec', 'coerceBigInts', 'removeInvalidPatterns'],
        properties: {
            title: {
                widget: 'singleLineText'
            },
            id: {
                widget: 'singleLineText'
            },
            namespace: {
                widget: 'singleLineText',
                instructions: 'A namespace to use for any generated types or queries. For example, if you enter `Foo` your types will be named `Foo_Bar`.'
            },
            endpoint: {
                widget: 'singleLineText'
            },
            spec: {
                widget: 'singleLineText'
            },
            coerceBigInts: {
                widget: 'singleLineText',
                instructions: 'Coerce integers larger than 32-bit to string for GraphQL compatibility. Specify comma separated globs of that match the names of properties to convert. Ex: id, ids, *_id, *_ids'
            },
            removeInvalidPatterns: {
                widget: 'checkbox'
            }
        }
    }))),
    getForms: value({
        ...healthCheckFormConfig,
        ...authenticationFormConfig
    }),
    getInitialData(serviceConfig) {
        const initialData = getInitialData(serviceProvider, serviceConfig);
        if (initialData.isNew) {
            initialData.healthCheck = {
                checkName: 'none'
            };
            initialData.authentication = {
                type: 'none'
            };
        }
        return initialData;
    },
    getConfig(formData) {
        const config = {
            title: formData.title ?? 'OpenAPI',
            namespace: formData.namespace ?? 'OpenAPI',
            provider: 'openapi',
            serviceType: 'openapi',
            authenticationType: 'none',
            options: {
                removeInvalidPatterns: formData.removeInvalidPatterns ?? true
            }
        };
        if (formData.endpoint) {
            set(config, ['options', 'endpoint'], formData.endpoint);
        }
        if (typeof formData.spec === 'string') {
            set(config, ['options', 'spec'], formData.spec);
            set(config, ['options', 'transformVersion'], CURRENT_OPEN_API_TRANSFORM_VERSION);
        }
        if (typeof formData.coerceBigInts === 'string') {
            set(config, ['options', 'coerceBigInts'], formData.coerceBigInts);
        }
        return updateHealthCheck(updateAuthentication(config, formData), formData);
    },
    isCompleteConfig(serviceConfig) {
        if (isMissingAuth(serviceConfig)) {
            return false;
        }
        return hasEveryKey(['options.endpoint', 'options.spec'], serviceConfig);
    },
    specTransform(doc, config) {
        let coerceBigintRegexes;
        if (typeof config.options?.coerceBigInts === 'string') {
            coerceBigintRegexes = config.options.coerceBigInts
                .split(',')
                .map((pattern) => makeRe(pattern.trim()))
                .filter(Boolean);
        }
        const res = transformSpec(doc, {
            coerceBigintRegexes,
            removeInvalidPatterns: config.options?.removeInvalidPatterns ?? false
        });
        // console.log(JSON.stringify(res));
        return res;
    },
    getHealthCheck: getHealthCheck()
};
export default serviceProvider;
